import { Button, Dropdown, IconButton, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

export interface IDocumentAssemblerVariableCardInlineAction {
  label: string;
  onClick: () => void;
}

export interface IDocumentAssemblerVariableCardAction {
  label: string;
  onClick: () => void;
  labelClassName?: string;
}

interface IDocumentAssemblerVariableCardProps {
  label: string;
  instruction: string;
  count: number;
  /** The literal the document carries today. Only the templatize flow knows one; the published view does not. */
  resolvedValue?: string | null;
  /** Shows how often the variable occurs on its own line, for the confirm step where no literal is known. */
  showMatchCountLine?: boolean;
  actions?: IDocumentAssemblerVariableCardAction[];
  /** Shown beside the token on hover or focus, e.g. "Mark as fixed". */
  inlineAction?: IDocumentAssemblerVariableCardInlineAction;
}

/**
 * One variable in the rail. With a resolved value it is the taller confirm-step card; without one it is the
 * shorter published-view card that shows only the token and how often it occurs.
 */
export const DocumentAssemblerVariableCard = ({
  label,
  instruction,
  count,
  resolvedValue,
  showMatchCountLine,
  actions,
  inlineAction,
}: IDocumentAssemblerVariableCardProps) => {
  const hasValueLine = Boolean(resolvedValue) || Boolean(showMatchCountLine);

  return (
    <div
      className={cn(
        'group/card flex w-full flex-col overflow-clip rounded-xl border border-border-secondary bg-bg-primary text-left transition-colors',
        'hover:border-border-tertiary focus-within:border-border-tertiary'
      )}
    >
      <div className="flex items-center gap-md p-lg">
        <p className="flex-1 truncate txt-extra-small-medium text-text-tertiary">
          {label}
        </p>
        {actions?.length ? (
          <Dropdown
            align="end"
            menu={{ groups: [{ items: actions }] }}
            contentClassName="w-[220px]"
          >
            <IconButton
              size="sm"
              aria-label={`Actions for ${label}`}
              className="opacity-0 focus-visible:opacity-100 group-focus-within/card:opacity-100 group-hover/card:opacity-100"
              icon={<Icons.DotsVertical className="size-4 text-fg-tertiary" />}
            />
          </Dropdown>
        ) : null}
      </div>

      <div className="h-px w-full bg-border-secondary" />

      <div className="flex w-full flex-col items-start gap-xs p-lg text-left">
        {hasValueLine ? (
          <div className="flex w-full items-center gap-md">
            <span className="flex-1 truncate txt-small-medium text-text-primary">
              {resolvedValue ?? label}
            </span>
            <span className="shrink-0 txt-extra-small-regular text-text-tertiary">
              {count} {count === 1 ? 'match' : 'matches'}
            </span>
          </div>
        ) : null}

        <div
          className={cn(
            'flex w-full items-start gap-sm',
            hasValueLine && 'pl-sm'
          )}
        >
          {hasValueLine ? (
            <Icons.CornerDownRight className="mt-md size-3 shrink-0 text-fg-tertiary" />
          ) : null}
          <div className="flex h-[28px] flex-1 items-center gap-sm overflow-clip rounded-sm bg-bg-secondary px-md">
            <span className="flex-1 truncate txt-extra-small-regular text-text-primary">
              {`{{${instruction}}}`}
            </span>
            {inlineAction ? (
              <Button
                variant="brand-link"
                size="xs"
                onClick={inlineAction.onClick}
                className="shrink-0 opacity-0 focus-visible:opacity-100 group-focus-within/card:opacity-100 group-hover/card:opacity-100"
              >
                {inlineAction.label}
              </Button>
            ) : null}
            {!hasValueLine ? (
              <span className="shrink-0 txt-extra-small-regular text-text-tertiary">
                {count}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
